<template>
  <lb-drawer
    :value="value"
    :heading="drawerHeading"
    :loading="loading"
    @input="$emit('input', $event)"
    data-testid="upload-response-drawer"
  >
    <template v-slot:body>
      <v-row>
        <!-- Request Description -->
        <v-col cols="12" class="font-weight-bold font-size-lg" data-testid="upload-response__description">
          {{ requestHeader.description }}
        </v-col>

        <!-- Requested From -->
        <v-col cols="3" class="gray--text">Requested From</v-col>
        <v-col cols="9" data-testid="upload-response__owner-name">
          {{ requestHeader.info_owner_detail?.name }}
        </v-col>

        <!-- Email Id -->
        <v-col cols="3" class="gray--text">Email Id</v-col>
        <v-col cols="9" class="blue--text text-decoration-underline" data-testid="upload-response__owner-email">
          {{ requestHeader.info_owner_detail?.email }}
        </v-col>

        <!-- Type -->
        <v-col cols="3" class="gray--text">Type</v-col>
        <v-col cols="9">
          <v-chip label class="lightgray radius-small" data-testid="upload-response__type-chip">
            <span>{{ requestHeader.type }}</span>
          </v-chip>
        </v-col>

        <!-- Priority -->
        <v-col cols="3" class="gray--text">Priority</v-col>
        <v-col cols="9">
          <v-chip
            label
            class="lightgray radius-small"
            v-if="requestHeader.priority"
            data-testid="upload-response__priority-chip"
          >
            <span>{{ requestHeader.priority }}</span>
          </v-chip>
        </v-col>

        <!-- Status -->
        <v-col cols="3" class="gray--text">Status</v-col>
        <v-col cols="9">
          <v-chip
            class="lightyellow yellow--text"
            v-if="requestHeader.stage === 0"
            data-testid="upload-response__status-chip--pending-submission"
          >
            <v-icon color="yellow">mdi-circle-small</v-icon>
            <span>Pending Submission</span>
          </v-chip>
          <v-chip
            class="lightblue blue--text"
            v-if="requestHeader.stage === 1"
            data-testid="upload-response__status-chip--pending-approval"
          >
            <v-icon color="blue">mdi-circle-small</v-icon>
            <span>Pending Approval</span>
          </v-chip>
          <v-chip
            class="lightred error--text"
            v-if="requestHeader.stage === -1"
            data-testid="upload-response__status-chip--cancelled"
          >
            <v-icon color="error">mdi-circle-small</v-icon>
            <span>Cancelled</span>
          </v-chip>
          <v-chip
            class="lightgreen green--text"
            v-if="requestHeader.stage === 2"
            data-testid="upload-response__status-chip--closed"
          >
            <v-icon color="green">mdi-circle-small</v-icon>
            <span>Closed</span>
          </v-chip>
        </v-col>

        <!-- File Upload Section -->
        <v-col cols="4" v-if="requestHeader.type === 'fileupload'">Upload Your File</v-col>
        <v-col cols="12" v-if="requestHeader.type === 'fileupload'">
          <DragDropfile
            hidedetails
            :files="uploadedFiles"
            @update:files="$emit('update-files', $event)"
            data-testid="upload-response__file-drop"
          />
        </v-col>

        <!-- Text Response Section -->
        <v-col cols="4" v-if="requestHeader.type === 'text'" class="pb-0">Add Response</v-col>
        <v-col cols="12" v-if="requestHeader.type === 'text'">
          <lb-textBox
            :value="response.response"
            @input="updateResponseField('response', $event)"
            label=""
            requriedrows="3"
            placeholder="Enter response"
            :hidedetails="true"
            :isRequired="true"
            data-testid="upload-response__response-text"
          />
        </v-col>

        <!-- Remark Section -->
        <v-col class="pb-0">Remark</v-col>
        <v-col cols="12">
          <lb-textBox
            :value="response.remark"
            @input="updateResponseField('remark', $event)"
            label=""
            requriedrows="2"
            placeholder="Enter remark"
            :hidedetails="true"
            :isRequired="true"
            data-testid="upload-response__remark-text"
          />
        </v-col>

        <!-- Uploaded Files Display -->
        <v-col cols="12">
          <v-card
            class="d-flex align-center justify-space-between border-on shadow-off pa-2 mb-2"
            style="min-width: 300px; max-width: 400px;"
            v-for="(item, index) in uploadedFiles"
            :key="index"
            data-testid="upload-response__uploaded-file"
          >
            <p class="ma-0 black--text font-size-md pl-2 text-truncate" style="flex: 1; overflow: hidden;" data-testid="upload-response__file-name">{{ item?.name }}</p>
            <v-btn icon @click="$emit('remove-file', index)" data-testid="upload-response__remove-file-btn">
              <v-icon color="error">mdi-delete</v-icon>
            </v-btn>
          </v-card>
        </v-col>

        <!-- Tabs Section -->
        <v-col cols="12" v-if="showTabs">
          <v-tabs
            v-model="activeTab"
            bg-color="primary"
            class="border-bottom d-flex align-center justify-center"
            color="caccounting"
            height="34"
          >
            <v-tab :key="0" class="text-transform-none pa-0 px-2 justify-start">
              Recent Activity
              <v-icon class="mr-2">mdi-information-outline</v-icon>
            </v-tab>
            <v-tab
              :key="1"
              class="text-transform-none pa-0 px-2 justify-start"
              @click="$emit('open-comments')"
              data-testid="upload-response__comments-tab"
            >
              Comments
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- Recent Activity Tab -->
            <v-tab-item :key="0">
              <v-container class="pa-0">
                <div class="d-flex flex-row flex-wrap">
                  <!-- File Detail Card -->
                  <template v-if="headerAttachments.length > 0">
                    <div
                      v-for="(file, fileIdx) in headerAttachments"
                      :key="resolveAttachmentKey(file, fileIdx)"
                      class="pa-3 mt-2 mr-1 border-on radius-small d-flex flex-column"
                      style="flex: 0 0 32%; max-width: 32%;"
                      :data-testid="`upload-response__attachment-card--${fileIdx}`"
                    >
                      <div class="d-flex justify-space-between align-center mb-2">
                        <p class="ma-0 black--text font-size-md text-truncate" style="flex: 1; min-width: 0; padding-right: 8px;" data-testid="upload-response__attachment-name">
                          {{ file.uploadname || file.originalname || file.file_name || `Document ${fileIdx + 1}` }}
                        </p>
                        <!-- <v-btn 
                          icon 
                          @click="$emit('delete-attachment', file, fileIdx)" 
                          :data-testid="`upload-response__delete-file-btn--${fileIdx}`"
                        >
                          <v-icon color="error">mdi-delete</v-icon>
                        </v-btn> -->
                      </div>
                      <p class="font-size-xm ma-0" v-if="file.date">{{ formatDate(file.date) }}</p>
                      <div class="mt-2">
                        <v-btn
                          @click="$emit('download-file', resolveAttachmentId(file))"
                          icon
                          class="cursor-pointer radius-mini border-on"
                          :data-testid="`upload-response__download-file-btn--${fileIdx}`"
                        >
                          <v-icon>mdi-download</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </template>

                  <!-- Response Card -->
                  <div v-if="requestHeader && requestHeader.response && typeof requestHeader.response === 'string' && requestHeader.response.trim()" class="pa-3 mt-2 mr-1 border-on radius-small d-flex flex-column gap-2" style="flex: 0 0 32%; max-width: 32%;" data-testid="upload-response__activity-response-card">
                    <p class="ma-0 black--text font-size-md mb-3">Response</p>
                    <div class="blue--text" data-testid="upload-response__response-display">{{ requestHeader.response }}</div>
                  </div>

                  <!-- Remark Card -->
                  <div v-if="requestHeader && requestHeader.remark" class="pa-3 mt-2 border-on radius-small d-flex flex-column gap-2" style="flex: 0 0 32%; max-width: 32%;" data-testid="upload-response__activity-remark-card">
                    <p class="ma-0 black--text font-size-md mb-3">Remark</p>
                    <div class="blue--text" data-testid="upload-response__remark-display">{{ requestHeader.remark }}</div>
                  </div>
                </div>
              </v-container>
            </v-tab-item>

            <!-- Comments Tab -->
            <v-tab-item :key="1" class="all-height">
              <v-card-text
                class="border-on d-flex flex-column all-height justify-space-between comments-container overflow-scroll pa-2"
              >
                <div v-if="(requestHeader.comments || []).length === 0" class="title text-center my-5 grey--text">
                  No Comments added yet
                </div>
                <div v-else class="mb-8">
                  <perfect-scrollbar>
                    <div
                      v-for="(comment, idx) in requestHeader.comments"
                      :key="idx"
                      :class="`d-flex all-width ${currentUserId !== comment.user ? '' : 'flex-column justify-end'}`"
                    >
                      <div class="mx-1 caption d-flex align-center justify-end">
                        {{ formatDateTime(comment.created_at) }}
                        <v-avatar size="24" class="lightpurple caccounting--text pa-2 ml-2">
                          {{ getUserInitials(comment.user) }}
                        </v-avatar>
                      </div>
                      <div
                        :class="`lightpurple radius-small px-4 py-2 my-2 ${currentUserId !== comment.user ? 'grey lighten-2' : 'text-left'}`"
                      >
                        {{ comment?.comment }}
                      </div>
                    </div>
                  </perfect-scrollbar>
                </div>

                <!-- Comment Input -->
                <div
                  v-if="canAddComments"
                  class="d-flex align-center"
                  :disabled="isDisabledStage"
                  style="position: absolute; bottom: 2%; width: 95%; background: white"
                >
                  <lb-string
                    :value="commentText"
                    @input="$emit('update-comment', $event)"
                    @keyup.enter.native="handleCommentKeyPress"
                    label="Write here..."
                    :error="commentError"
                    hidedetails
                    data-testid="upload-response__comment-input"
                  />
                  <v-btn
                    small
                    color="caccounting"
                    class="white--text ml-2"
                    @click="$emit('add-comment')"
                    :loading="commentsLoading"
                    data-testid="upload-response__send-comment-btn"
                  >
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </template>

    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn
        class="black white--text"
        :disabled="isSubmitDisabled"
        @click="$emit('upload')"
        data-testid="upload-response__upload-btn"
      >
        <span>{{ buttonText }}</span>
      </v-btn>
    </template>
  </lb-drawer>
</template>

<script>
import DragDropfile from '@/views/Inputs/DragDropfile.vue';

export default {
  name: 'UploadResponseDrawer',
  components: {
    DragDropfile,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showTabs: {
      type: Boolean,
      default: true,
    },
    requestHeader: {
      type: Object,
      default: () => ({}),
    },
    response: {
      type: Object,
      default: () => ({ response: '', remark: '' }),
    },
    uploadedFiles: {
      type: Array,
      default: () => [],
    },
    currentUserId: {
      type: String,
      default: '',
    },
    canAddComments: {
      type: Boolean,
      default: false,
    },
    commentText: {
      type: String,
      default: '',
    },
    commentError: {
      type: String,
      default: '',
    },
    commentsLoading: {
      type: Boolean,
      default: false,
    },
    userList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTab: 0,
    };
  },
  computed: {
    /**
     * Determines if the stage is disabled (rejected or closed)
     */
    isDisabledStage() {
      return [-1, 2].indexOf(this.requestHeader.stage) > -1;
    },
    /**
     * Dynamic drawer heading based on showTabs prop
     */
    drawerHeading() {
      return this.showTabs ? 'Add/Edit Response' : 'Upload';
    },
    /**
     * Dynamic button text based on showTabs prop
     */
    buttonText() {
      return this.showTabs ? 'Update' : 'Upload';
    },
    /**
     * Determines if submit button should be disabled based on request type and data
     */
    isSubmitDisabled() {
      const header = this.requestHeader;
      const response = this.response;
      const files = this.uploadedFiles;

      // For file upload type, at least one file is required
      if (header && header.type === 'fileupload') {
        return !files || !Array.isArray(files) || files.length === 0;
      }

      // For text type, response is required
      if (header && header.type === 'text') {
        return !response || !response.response || !response.response.trim();
      }

      return false;
    },
    /**
     * Resolves attachment entries from various possible field structures
     */
    headerAttachments() {
      const source = this.requestHeader || {};
      const attachments = [];
      const candidates = [source.fileid_detail, source.fileid_details, source.file_details, source.upload_v2];
      candidates.forEach(entry => {
        if (Array.isArray(entry)) {
          attachments.push(...entry.filter(Boolean));
        } else if (entry && typeof entry === 'object') {
          attachments.push(entry);
        }
      });
      if (attachments.length === 0 && Array.isArray(source.fileid)) {
        source.fileid.filter(Boolean).forEach((id, index) => {
          attachments.push({
            _id: id,
            fileid: id,
            originalname: `Document ${index + 1}`,
          });
        });
      }
      return attachments;
    },
  },
  methods: {
    /**
     * Handles Enter key press in comment input
     */
    handleCommentKeyPress(event) {
      if (event && !event.shiftKey && this.commentText && this.commentText.trim()) {
        event.preventDefault();
        this.$emit('add-comment');
      }
    },
    /**
     * Updates a specific field in the response object
     * @param {string} field - The field name to update
     * @param {any} value - The new value
     */
    updateResponseField(field, value) {
      this.$emit('update-response', { field, value });
    },

    /**
     * Formats a date using Nova formatter
     * @param {string|Date} date - The date to format
     * @returns {string} Formatted date string
     */
    formatDate(date) {
      return this.$nova.formatDate(date);
    },

    /**
     * Formats a datetime using Nova formatter
     * @param {string|Date} date - The datetime to format
     * @returns {string} Formatted datetime string
     */
    formatDateTime(date) {
      return this.$nova.formatDateTime(date, 'DD MMM yyyy hh:mm A');
    },

    /**
     * Gets user initials from user ID
     * @param {string} userId - The user ID
     * @returns {string} User initials or 'NA'
     */
    getUserInitials(userId) {
      const user = this.userList.find((u) => u._id === userId);
      if (!user || !user.name) return 'NA';
      return user.name.charAt(0).toUpperCase();
    },
    resolveAttachmentId(file) {
      if (!file) return null;
      if (Array.isArray(file.fileid)) return (file.fileid.find(Boolean)) || null;
      return file._id || file.fileid || file.file_id || file.id || null;
    },
    resolveAttachmentKey(file, index) {
      return file?._id || file?.fileid || file?.file_id || file?.id || `attachment-${index}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.comments-container {
  height: 30vh;
}
</style>
